/**
 * Display formatters used across the CRM.
 * Centralize them so the UI is consistent and we change one thing.
 */

/**
 * "08:00:00" -> "8:00 AM". Empty string for null/invalid input.
 */
export function formatTime(hhmmss: string | null | undefined): string {
  if (!hhmmss) return "";
  const parts = hhmmss.split(":");
  if (parts.length < 2) return hhmmss;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmmss;
  const ampm = h < 12 ? "AM" : "PM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

/**
 * American MM/DD/YYYY (Seth 2026-06-24 — all Pegasus dates). Handles a
 * "YYYY-MM-DD" date string WITHOUT a timezone shift (parsing the raw string as
 * a Date would treat it as UTC midnight and slip a day in US time zones).
 *   "2026-06-24" -> "06/24/2026"
 */
export function formatDateUS(d: string | Date | null | undefined): string {
  if (!d) return "";
  if (typeof d === "string") {
    const m = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[2]}/${m[3]}/${m[1]}`;
    const parsed = new Date(d);
    return Number.isNaN(parsed.getTime()) ? d : formatDateUS(parsed);
  }
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
}

/**
 * A full timestamp for the activity log: "06/24/2026 12:47:21 PM" (local time).
 */
export function formatDateTimeUS(d: string | Date | null | undefined): string {
  if (!d) return "";
  const dt = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(dt.getTime())) return typeof d === "string" ? d : "";
  const time = dt.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
  return `${formatDateUS(dt)} ${time}`;
}

/**
 * Customer display name. Prefers location name, falls back to customer fullname.
 * Strips trailing customer numbers and flips "Lastname,Firstname" to "Firstname Lastname".
 *
 * Examples:
 *   "Simonson,Seth 1011072" -> "Seth Simonson"
 *   "Woodward,Carol 1008535" -> "Carol Woodward"
 *   "Verde Property Management" -> "Verde Property Management"
 *   "ABC Company, LLC" -> "ABC Company, LLC" (space after comma = business, leave as-is)
 */
export function displayCustomerName(
  locname: string | null | undefined,
  fullname: string | null | undefined,
): string {
  if (locname && locname.trim()) return locname.trim();
  if (!fullname) return "Customer";
  const cleaned = fullname.replace(/\s*\d{7}\s*$/, "").trim();
  // Only flip "Lastname,Firstname" - no space after the comma is the ESC
  // convention for personal names. Business names use ", LLC" / ", Inc"
  // (with a space) and shouldn't be flipped.
  const match = cleaned.match(/^([^,]+),([^ ].*)$/);
  if (match) {
    const before = match[1].trim();
    const after = match[2].trim();
    // Two-surname household crammed into ESC's one field (Seth, 2026-06-04):
    // "Jane Doyle,Eric Hockert" — a real personal name has ONE word before
    // the comma; when BOTH sides are full names (2+ words each), it's two
    // people → "Eric Hockert & Jane Doyle". The structured fix is
    // CUST-NAMES-01 at customer-entry time.
    if (before.split(/\s+/).length >= 2 && after.split(/\s+/).length >= 2) {
      return `${after} & ${before}`;
    }
    return `${after} ${before}`;
  }
  return cleaned || "Customer";
}

/**
 * AFTERHOURS-POLISH-01: the answering service writes cities in ALL CAPS
 * ("EDINA"); ESC and Pegasus write them cased. Title-case a string ONLY when
 * it's all-shouty — anything a human already cased passes through untouched
 * (so "White Bear Lake" and "McKnight Rd" never get mangled).
 */
export function titleCaseIfShouty(s: string | null | undefined): string | null {
  const v = (s ?? "").trim();
  if (!v) return null;
  if (v !== v.toUpperCase()) return v;
  return v.toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase());
}

/**
 * Compose a one-line street address from the dispatch's location fields.
 */
export function displayAddress(
  street: string | null | undefined,
  city: string | null | undefined,
): string {
  if (!street) return "";
  if (city) return `${street}, ${city}`;
  return street;
}

/** ESC's customer-facing arrival windows. Order matters for matching. */
export const TIME_WINDOWS = ["8AM", "8-12", "10-2", "12-4", "3-7"] as const;

// Install arrival windows (Seth 2026-06-30) — install crews run their own
// windows, not the 8-12/12-4 service rhythm. "12-4" overlaps the service set;
// "7-8" / "11-1" are install-only. Shared by the booking modal AND the board's
// quick-edit so there's ONE source of truth. (Plain string[] so .includes(win)
// type-checks against a free-text scheduled_window.)
export const INSTALL_TIME_WINDOWS: string[] = ["7-8", "11-1", "12-4"];
export const INSTALL_ONLY_WINDOWS: string[] = ["7-8", "11-1"];
export const INSTALL_WINDOW_LABELS: Record<string, string> = { "7-8": "7–8 AM", "11-1": "11 AM – 1 PM", "12-4": "12 – 4 PM" };

/**
 * Format an estimate (in minutes) as "X min" or "Xh Ym".
 * Both ESC TechTime fields (Dispatch + DispTech) are stored in minutes
 * despite the confusing tech_est_hours field name in our view.
 *
 *   45  -> "45 min"
 *   60  -> "1h"
 *   90  -> "1h 30m"
 *   180 -> "3h"
 */
export function formatDuration(
  minutes: number | null | undefined,
): string {
  if (!minutes || minutes <= 0) return "";
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

/**
 * Detect whether a window/priority code is effectively blank.
 * ESC uses "-----" (dashes) as the literal default/empty priority value.
 */
function isBlankCode(code: string | null | undefined): boolean {
  if (!code) return true;
  const trimmed = code.trim();
  if (trimmed === "") return true;
  // Dashes-only (any length): "-", "--", "-----", etc.
  if (/^-+$/.test(trimmed)) return true;
  return false;
}

/**
 * Figure out which window/priority code to display for a dispatch.
 *
 * ESC's Priority field can hold either:
 *   - A time window: 8AM, 8-12, 10-2, 12-4, 3-7
 *   - A special code: LONG, TECH, PART, QUOTE, PAIR, REBATE, PLUMB, AFTER, INSTAL
 *   - Blank/dashes (default): means "regular service call with no special handling"
 *
 * Cascade (correct order):
 *   1. Priority is a time window -> use it
 *   2. Priority is a non-blank special code (TECH, LONG, etc.) -> RESPECT IT.
 *      Don't override with notes-derived window - the priority code carries
 *      important meaning (locked tech, long job, paired calls, etc.)
 *   3. Priority is blank -> look at notes for a leading time window
 *   4. Nothing -> null
 *
 * Returns: { value, isTimeWindow } so the UI can style time windows vs.
 * special codes differently.
 */
export function resolveWindow(
  windowCode: string | null | undefined,
  notes: string | null | undefined,
): { value: string | null; isTimeWindow: boolean } {
  const isBlank = isBlankCode(windowCode);

  if (!isBlank && windowCode) {
    const upper = windowCode.toUpperCase().trim();
    // Step 1: is the priority itself a time window?
    if ((TIME_WINDOWS as readonly string[]).includes(upper)) {
      return { value: upper, isTimeWindow: true };
    }
    // Step 2: respect non-time-window special priority codes
    return { value: upper, isTimeWindow: false };
  }

  // Step 3: priority is blank - look at the leading notes for a time window
  if (notes) {
    const trimmed = notes.trimStart();
    for (const w of TIME_WINDOWS) {
      const re = new RegExp(
        `^${w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(\\b|[\\s,*]|$)`,
      );
      if (re.test(trimmed)) {
        return { value: w, isTimeWindow: true };
      }
    }
  }

  return { value: null, isTimeWindow: false };
}

/**
 * Like resolveWindow but separates out the customer-facing time window
 * (from notes when present) from the special priority code. Returns both
 * independently so the UI can show "TECH 10-2" or "PART 8-12" etc.
 *
 * - timeWindow: the customer arrival window (8AM, 8-12, 10-2, 12-4, 3-7) or null
 * - priority: the non-window priority code (TECH, PART, LONG, QUOTE, etc.) or null
 * - When priority IS a time window, priority=null and timeWindow=that code.
 */
export function resolveWindowAndPriority(
  windowCode: string | null | undefined,
  notes: string | null | undefined,
): { timeWindow: string | null; priority: string | null } {
  const isBlank = isBlankCode(windowCode);
  let timeWindow: string | null = null;
  let priority: string | null = null;

  if (!isBlank && windowCode) {
    const upper = windowCode.toUpperCase().trim();
    if ((TIME_WINDOWS as readonly string[]).includes(upper)) {
      timeWindow = upper;
    } else {
      priority = upper;
    }
  }

  // Look at leading notes for a time window (regardless of priority,
  // because dispatcher may set priority=TECH with notes starting "10-2, ..."
  // meaning the call is tech-locked AND has a 10-2 customer window)
  if (!timeWindow && notes) {
    const trimmed = notes.trimStart();
    for (const w of TIME_WINDOWS) {
      const re = new RegExp(
        `^${w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(\\b|[\\s,*]|$)`,
      );
      if (re.test(trimmed)) {
        timeWindow = w;
        break;
      }
    }
  }

  return { timeWindow, priority };
}

/**
 * Extract a "what is the tech doing" preview from dispatch notes.
 * ESC notes typically start with patterns like "8-12, 5 of 6, PLUS AC CK, ACC AT FRN CK, ..."
 * We strip the window code and X-of-Y prefixes, then take the first meaningful chunk.
 */
export function extractJobPreview(
  notes: string | null | undefined,
  maxLen: number = 40,
): string {
  if (!notes) return "—";
  let cleaned = notes.trim();
  // Strip leading window code (8AM, 8-12, 10-2, 12-4, 3-7) + comma + space
  cleaned = cleaned.replace(/^(8AM|8-12|10-2|12-4|3-7)[,\s]+/i, "");
  // Strip "X of Y" pattern
  cleaned = cleaned.replace(/^\d+\s*of\s*\d+[,\s]+/i, "");
  // Strip "start" prefix sometimes used
  cleaned = cleaned.replace(/^(?:\d{1,2}am\s*)?start[,\s]+/i, "");
  cleaned = cleaned.trim();
  if (!cleaned) return "—";
  if (cleaned.length <= maxLen) return cleaned;
  // Try to break at a comma so we don't truncate mid-phrase
  const sliced = cleaned.slice(0, maxLen);
  const commaIdx = sliced.lastIndexOf(",");
  if (commaIdx > 15) return sliced.slice(0, commaIdx) + "…";
  return sliced.trim() + "…";
}
